import { AveragePensionableEarningsInfoComponent } from './../dialogs/average-pensionable-earnings-info/average-pensionable-earnings-info.component';
import { CalculatorService } from './../../service/calculator.service';
import { Component, NgZone, OnInit } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { Subject, of, take, takeUntil } from 'rxjs';
import { ImportData, PensionProjections } from 'src/app/models/UserInfo';
import { AuthStateService } from 'src/app/service/auth-state.service';
import { UserService } from 'src/app/service/user.service';
import { InfoDialogComponent } from '../dialogs/info-dialog/info-dialog';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { ProjectionResults } from 'src/app/models/ProjectionResults';
import { AveragePensionableEarningsEspsInfoComponent } from '../dialogs/average-pensionable-earnings-esps-info/average-pensionable-earnings-esps-info.component';
import { YearlySalaryInfoComponent } from '../dialogs/yearly-salary-info/yearly-salary-info.component';
import { FuturePercentageInfoComponent } from '../dialogs/future-percentage-info/future-percentage-info.component';
import { EffectiveDateInfoComponent } from '../dialogs/effective-date-info/effective-date-info.component';
import { FormOfPensionPaymentMarriedInfoComponent } from '../dialogs/form-of-pension-payment-married-info/form-of-pension-payment-married-info.component';
import { FormOfPensionPaymentInfoComponent } from '../dialogs/form-of-pension-payment-info/form-of-pension-payment-info.component';
import { AddRetirementDateInfoComponent } from '../dialogs/add-retirement-date-info/add-retirement-date-info.component';
import { AveragePensionableEarningsSpsInfoComponent } from '../dialogs/average-pensionable-earnings-sps-info/average-pensionable-earnings-sps-info.component';
import { ComparisonRetirementTypesComponent } from '../dialogs/comparison-retirement-types/comparison-retirement-types.component';
import { DepartureDateComponent } from '../dialogs/departure-date/departure-date.component';
import { CurrencyPipe } from '@angular/common';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { ComparisonRetirementTypesEspsComponent } from '../dialogs/comparison-retirement-types-esps/comparison-retirement-types-esps.component';

@Component({
  selector: 'app-calculator-results',
  templateUrl: './calculator-results.component.html',
  styleUrls: ['./calculator-results.component.scss'],
})
export class CalculatorResultsComponent implements OnInit {
  public activeAccount: AccountInfo | undefined;
  public currentUser!: ImportData;
  public retirementEvents!: any;
  public terminationBenefits!: any;
  private destroy$ = new Subject<void>();
  public graphData: any[] = [];
  public projectTableData: PensionProjections[] = [];
  public departurePensionData: any;
  public calculatorInputForm!: FormGroup;
  public root!: am5.Root;
  private chart!: am5xy.XYChart;
  private legend!: am5.Legend;
  public isPost65: boolean = false;
  public selectedOptionalBenefits: any;
  public optionalBenefitsColumns: string[] = ['desc', 'Post65Total'];
  public displayedColumns: string[] = ['retirementType', 'age', 'date'];
  public deperatureDisplayedColumns: string[] = ['column1', 'column2'];
  public isCalculating: boolean = false;
  public isPortrait: boolean = false;
  public inPrint: boolean = false;
  public indicator!: am5.Container;
  public hourglassanimation!: any;
  private hasCalculated = false;
  private initialCalculationTriggered = false;
  private isDestroyed = false;

  constructor(
    private authStateService: AuthStateService,
    private userService: UserService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private calculatorService: CalculatorService,
    private currencyPipe: CurrencyPipe,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    const today = new Date();
    this.checkOrientationAndWidth();

    window.matchMedia("(orientation: portrait)").addEventListener("change", () => {
      this.ngZone.run(() => {
        this.checkOrientationAndWidth();
      });
    });

    window.addEventListener("resize", () => {
      this.ngZone.run(() => {
        this.checkOrientationAndWidth();
      });
    });

    this.userService.currentUser$.subscribe((result) => {
      if (result.Username) {
        this.currentUser = result;

        if (this.hasSpouse()) {
          this.optionalBenefitsColumns.push('SurvivorPost65Total');
        }

        this.isPost65 = this.getAge(this.currentUser.DOB.toString());
        const minEffectiveDate = this.getDateWhenFiftyFive(
          this.currentUser.DOB.toString()
        );
        const maxEffectiveDate = this.getDateWhenSeventyOne(
          this.currentUser.DOB.toString()
        );
        const calcDate = this.currentUser.CalcDate.toString();
        const effectDate = new Date(calcDate);
        effectDate.setDate(effectDate.getDate() + 1);

        this.calculatorInputForm = this.fb.group({
          rppEarnings: [this.getRppEarningsValue(), [Validators.required]],
          espsEarnings: [
            this.currentUser.ESPSHigh3.toFixed(2),
            [Validators.required],
          ],
          yearlySalaryIncrease: [
            0,
            [Validators.required, Validators.min(0), Validators.max(100)],
          ],
          futurePercentageOfWork: [
            100,
            [Validators.required, Validators.min(0), Validators.max(100)],
          ],
          effectiveDate: [
            effectDate,
            [Validators.required, this.getValidEffectiveDate(calcDate)],
          ],
          isLeavingNwmo: [false, Validators.required],
          departureDate: [
            null,
            this.dateRangeValidator(today, minEffectiveDate),
          ],
          customRetDate: [
            null,
            [
              this.dateRangeValidator(minEffectiveDate, maxEffectiveDate),
              this.checkProjectionRetirementDates(),
            ],
          ],
        });

        this.calculatorInputForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.initialCalculationTriggered = true;
          this.runCalculations(); 
        });

        setTimeout(() => {
          if (!this.initialCalculationTriggered) {
            this.runCalculations(); 
          }
        }, 500);
      }
    });
  }

  public runCalculations() {
    if (this.calculatorInputForm.valid) {
      if (this.indicator) this.toggleIndicator(true);
      this.isCalculating = true;
      this.selectedOptionalBenefits = null;
      this.calculatorService
        .calculate({
          username: this.currentUser.Username,
          rPP: this.calculatorInputForm.get('rppEarnings')?.value,
          eSPS: this.calculatorInputForm.get('espsEarnings')?.value,
          annualSalaryIncrease:
            this.calculatorInputForm.get('yearlySalaryIncrease')?.value / 100,
          futurePercentOfWork:
            this.calculatorInputForm.get('futurePercentageOfWork')?.value / 100,
          effectiveDate: this.calculatorInputForm.get('effectiveDate')?.value,
          departureDate:
            this.calculatorInputForm.get('isLeavingNwmo')?.value === false
              ? undefined
              : this.calculatorInputForm.get('departureDate')?.value
              ? new Date(this.calculatorInputForm.get('departureDate')?.value)
              : undefined,
          customRetDate: this.calculatorInputForm.get('customRetDate')?.value
            ? new Date(this.calculatorInputForm.get('customRetDate')?.value)
            : undefined,
        })
        .pipe(take(1))
        .subscribe((result) => {
          this.retirementEvents = result;
          if (
            this.calculatorInputForm.get('departureDate')?.value &&
            this.calculatorInputForm.get('isLeavingNwmo')?.value
          ) {
            this.chart.dispose();
            this.terminationBenefits = result.TerminationBenefits[0];
            if (result.EPSFlag) {
              this.departurePensionData = [
                {
                  column1: 'Deferred pension*',
                  column2: `An unreduced pension of <b>${this.currencyPipe.transform(
                    this.terminationBenefits.UnreducedPensionTotal,
                    'USD',
                    'symbol',
                    '1.0-0'
                  )}</b> per month (payable for your lifetime), can be elected and commenced by <b>${this.formatDateFullMonth(
                    this.terminationBenefits.NRD
                  )}</b>.`,
                },
                {
                  column1: 'Earliest retirement date with an unreduced pension',
                  column2: `Under the supplementary pension plan, you are eligible to commence an unreduced lifetime pension as early as <b>${this.formatDateFullMonth(
                    this.terminationBenefits.EURD
                  )}</b>.`,
                },
                {
                  column1: 'Earliest retirement date with a reduced pension',
                  column2: `Subject to the early retirement provisions of the registered pension plan and the supplementary pension plan, you can commence the lifetime pension as early as <b>${this.formatDateFullMonth(
                    this.terminationBenefits.ERD
                  )}</b>, along with the bridge pension, both amounts subject to any applicable reductions.`,
                },
                {
                  column1: 'Bridge to age 65',
                  column2: `A bridge pension of <b>${this.currencyPipe.transform(
                    this.terminationBenefits.BridgePension,
                    'USD',
                    'symbol',
                    '1.0-0'
                  )}</b> per month is payable until <b>${this.formatDateFullMonth(
                    this.terminationBenefits.BridgeEndDate
                  )}</b>, subject to any applicable reductions.`,
                },
                {
                  column1: 'RPP versus SPP',
                  column2: `Of the unreduced amount payable to you at age 65, only <b>${this.currencyPipe.transform(
                    this.terminationBenefits.UnreducedPensionRPP,
                    'USD',
                    'symbol',
                    '1.0-0'
                  )}</b> per month may be paid from the registered pension plan with the balance being paid from the supplementary pension plan. The bridge pension will be payable from the registered pension plan.`,
                },
                {
                  column1: 'Commuted Value Option',
                  column2:
                    'You may be entitled to transfer a lump-sum value of your registered deferred pension at the time of termination.',
                },
              ];
            } else {
              this.departurePensionData = [
                {
                  column1: 'Deferred pension*',
                  column2: `An unreduced pension of <b>${this.currencyPipe.transform(
                    this.terminationBenefits.UnreducedPensionTotal,
                    'USD',
                    'symbol',
                    '1.0-0'
                  )}</b> per month (payable for your lifetime), can be elected and commenced by <b>${this.formatDateFullMonth(
                    this.terminationBenefits.NRD
                  )}</b>.`,
                },
                {
                  column1: 'Earliest retirement date with a reduced pension',
                  column2: `Subject to the early retirement provisions of the registered pension plan and the supplementary pension plan, you can commence the lifetime pension as early as <b>${this.formatDateFullMonth(
                    this.terminationBenefits.ERD
                  )}</b>, along with the bridge pension, both amounts subject to any applicable reductions.`,
                },
                {
                  column1: 'Bridge to age 65',
                  column2: `A bridge pension of <b>${this.currencyPipe.transform(
                    this.terminationBenefits.BridgePension,
                    'USD',
                    'symbol',
                    '1.0-0'
                  )}</b> per month is payable until <b>${this.formatDateFullMonth(
                    this.terminationBenefits.BridgeEndDate
                  )}</b>, subject to any applicable reductions.`,
                },
                {
                  column1: 'Commuted Value Option',
                  column2:
                    'You may be entitled to transfer a lump-sum value of your registered deferred pension at the time of termination.',
                },
              ];
            }
            this.isCalculating = false;
          } else {
            this.retirementEvents.RetirementEvents.sort((a: any, b: any) => {
              return (
                new Date(a.RetirementDate).getTime() -
                new Date(b.RetirementDate).getTime()
              );
            });
            this.graphData = [];
            this.projectTableData = [];
            this.mapGraphData();
            this.mapPensionProjectionTableData();
            this.isCalculating = false;
            if (!this.isDestroyed) {
              this.generateGraph();
            }
          }
        });
    }
  }

  private checkOrientationAndWidth() {
    const isPortrait = window.matchMedia('(orientation: portrait)').matches;
    const isMobileWidth = window.innerWidth < 750;

    if (isPortrait && isMobileWidth) {
      this.isPortrait = true;
    } else {
      this.isPortrait = false;
      if (this.retirementEvents && !this.inPrint) {
        this.generateGraph();
      }
    }
  }

  getDateWhenFiftyFive(dob: string) {
    const dateOfBirth = new Date(dob);
    const fiftyFifthBirthday = new Date(
      dateOfBirth.getFullYear() + 55,
      dateOfBirth.getMonth(),
      dateOfBirth.getDate()
    );
    const today = new Date();

    if (fiftyFifthBirthday <= today) {
      return today;
    } else {
      return fiftyFifthBirthday;
    }
  }
  getDateWhenSeventyOne(dob: string) {
    const dateOfBirth = new Date(dob);
    var maxYear = dateOfBirth.getFullYear() + 71;
    if (dateOfBirth.getMonth() == 11 && dateOfBirth.getDate() > 1) {
      maxYear++;
    }
    return new Date(maxYear, 11, 1);
  }

  extractRetirementDates(): Date[] {
    return this.retirementEvents.RetirementEvents.map(
      (event: { RetirementDate: any }) => new Date(event.RetirementDate)
    );
  }

  checkProjectionRetirementDates() {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      const dates = this.extractRetirementDates().map((date) =>
        new Date(date).getTime()
      );
      const controlDateString = control.value.getTime();
      const dateExists = dates.includes(controlDateString);
      return dateExists ? { dateExists: { value: control.value } } : null;
    };
  }

  dateRangeValidator(minDate: Date, maxDate: Date): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }
      const rangeValid = !(
        control.value >= minDate && control.value <= maxDate
      );
      return rangeValid ? { dateRange: { value: control.value } } : null;
    };
  }
  getValidEffectiveDate(minDateString: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const minDate = new Date(minDateString);

      const controlDate = new Date(control.value);
      const isValid = controlDate >= minDate;
      return isValid
        ? null
        : {
            dateMin: {
              valid: false,
              minDate: minDate.toISOString(),
              enteredDate: control.value,
            },
          };
    };
  }

  setAndReturnCurrentUser(result: any) {
    this.currentUser = result;
    return of(result);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.chart) {
      this.chart.dispose();
    }
    if (this.root) {
      this.root.dispose();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  deleteCustomRetDate() {
    this.calculatorInputForm.patchValue({
      customRetDate: null,
    });
  }

  getRppEarningsValue() {
    if (
      this.currentUser.Representation === 'Management' &&
      new Date(this.currentUser.DOE) < new Date('January 1, 2021')
    ) {
      return this.currentUser.High3.toFixed(2);
    } else if (
      this.currentUser.Representation === 'Society' &&
      new Date(this.currentUser.DOE) < new Date('January 1, 2021')
    ) {
      return this.currentUser.High3.toFixed(2);
    } else if (
      this.currentUser.Representation === 'PWU' &&
      new Date(this.currentUser.DOE) < new Date('April 1, 2022')
    ) {
      return this.currentUser.High3.toFixed(2);
    } else {
      return this.currentUser.High5.toFixed(2);
    }
  }

  hasSpouse(): boolean {
    return (
      !!this.currentUser.SPSDOB && this.currentUser.SPSDOB !== 'January 0, 1900'
    );
  }

  mapGraphData() {
    this.retirementEvents.RetirementEvents.forEach((item: any) => {
      let graphItem = <any>{};

      graphItem.year = this.formatDate(item.RetirementDate);
      graphItem.TypeText = item.RetirementTypeShort;

      graphItem.Pre65Bridge = item.RetirementBenefits.Pre65Bridge;
      graphItem.Pre65SERP = item.RetirementBenefits.Pre65SERP;
      graphItem.Pre65RPP = item.RetirementBenefits.Pre65RPP;

      graphItem.Post65Bridge = item.RetirementBenefits.Post65Bridge;
      graphItem.Post65RPP = item.RetirementBenefits.Post65RPP;
      graphItem.Post65SERP = item.RetirementBenefits.Post65SERP;

      graphItem.Post65Surivor = item.RetirementBenefits.Post65Surivor;
      graphItem.Post65SurivorSERP = item.RetirementBenefits.Post65SurivorSERP;

      graphItem.SurvivorPost65Total =
        item.RetirementBenefits.SurvivorPost65Total;
      graphItem.Pre65Total = item.RetirementBenefits.Pre65Total;
      graphItem.Post65Total = item.RetirementBenefits.Post65Total;

      this.graphData.push(graphItem);
    });
  }

  private getAge(birthday: string): boolean {
    const currentDate = new Date();
    const birthDate = new Date(birthday);

    let age = currentDate.getFullYear() - birthDate.getFullYear();

    const hasHadBirthdayThisYear =
      currentDate.getMonth() > birthDate.getMonth() ||
      (currentDate.getMonth() === birthDate.getMonth() &&
      currentDate.getDate() >= birthDate.getDate());

    if (!hasHadBirthdayThisYear) {
      age--;
    }

    return age >= 65;
  }

  mapPensionProjectionTableData() {
    this.retirementEvents.RetirementEvents.forEach((item: any) => {
      const tableItem = {
        retirementType: item.RetirementType,
        age: this.formatAge(item.RetirementAge),
        date: this.formatDateFullMonth(item.RetirementDate),
      };
      this.projectTableData.push(tableItem);
    });
  }

  formatAge(num: number) {
    if (Number.isInteger(num)) {
      return num + '.0';
    } else {
      return num.toString();
    }
  }

  formatDateFullMonth(dateString: string): string {
    const date = new Date(dateString);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${month} ${day} ${year}`;
  }

  getDepartureDate() {
    return this.formatDateFullMonth(
      this.calculatorInputForm.get('departureDate')?.value.toString()
    );
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sept',
      'Oct',
      'Nov',
      'Dec',
    ];

    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${month} ${day} ${year}`;
  }

  generateGraph() {
    if (this.root) {
      this.root.dispose();
      this.chart.dispose();
    }

    this.root = am5.Root.new('chartdiv');

    const responsive = am5themes_Responsive.new(this.root);
    this.root.setThemes([am5themes_Animated.new(this.root), responsive]);

    responsive.addRule({
      relevant: am5themes_Responsive.widthM,
      applying: () => {
        legend.setAll({
          layout: this.root.verticalLayout,
        });
      },
      removing: () => {
        legend.setAll({
          layout: this.root.horizontalLayout,
        });
      },
    });

    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        panX: false,
        panY: false,
        layout: this.root.verticalLayout,
      })
    );

    var data = this.graphData;

    var xRenderer = am5xy.AxisRendererX.new(this.root, {
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
      fill: am5.color(0x297373),
    });

    var xAxis = this.chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: 'year',
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(this.root, {}),
      })
    );

    xAxis.get('renderer').grid.template.setAll({
      strokeWidth: 0,
      strokeOpacity: 0,
      width: 100,
    });

    xAxis.children.push(
      am5.Label.new(this.root, {
        text: 'Retirement Date',
        x: am5.p50,
        centerX: am5.p50,
        fontSize: 16,
        fontWeight: '600',
      })
    );

    xAxis.labelsContainer.set(
      'tooltip',
      am5.Tooltip.new(this.root, {
        pointerOrientation: 'down',
      })
    );

    xRenderer.labels.template.setAll({
      oversizedBehavior: 'wrap',
      paddingTop: 40,
      html: '<div style="width:100%; text-align: center; cursor: pointer;"><p >{year}</p><p style="font-size: 10pt;">{TypeText}</p></div>',
      maxWidth: 200,
      height: 150,
      cursorOverStyle: 'pointer',
      interactive: true,
      background: am5.Rectangle.new(this.root, {
        fillOpacity: 0,
        strokeOpacity: 0,
      }),
    });

    xRenderer.labels.template.events.on('click', (event) => {
      this.highlighColumn(event);
    });

    var yRenderer = am5xy.AxisRendererY.new(this.root, {});
    yRenderer.labels.template.set('visible', false);

    var yAxis = this.chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        min: 0,
        renderer: yRenderer,
      })
    );

    yAxis.get('renderer').grid.template.setAll({
      strokeWidth: 0,
      strokeOpacity: 0,
    });

    yAxis.children.push(
      am5.Label.new(this.root, {
        text: 'Monthly NWMO Pension',
        rotation: -90,
        centerX: am5.p50,
        y: am5.p50,
      })
    );

    // Add legend
    var legend = this.chart.children.push(
      am5.Legend.new(this.root, {
        nameField: 'name',
        fillField: 'color',
        strokeField: 'color',
        centerX: am5.percent(40),
        x: am5.percent(50),
        marginTop: 20,
        layout: this.root.horizontalLayout,
      })
    );

    legend.labels.template.setAll({
      fontWeight: '500',
      paddingLeft: 10,
    });

    legend.markerRectangles.template.setAll({
      height: 20,
      width: 20,
      cornerRadiusTL: 0,
      cornerRadiusTR: 0,
      cornerRadiusBL: 0,
      cornerRadiusBR: 0,
    });

    legend.data.push({
      name: 'RPP',
      color: am5.color(this.getColor('RPP')),
    });

    if (this.retirementEvents.SPPFlag || this.retirementEvents.EPSFlag) {
      legend.data.push({
        name: 'SPP (ESPS)',
        color: am5.color(this.getColor('SERP')),
        html: 'SPP (ESPS)',
      });
    }

    legend.data.push({
      name: 'Bridge to Age 65',
      color: am5.color(this.getColor('BridgeTo65')),
    });

    xAxis.data.setAll(data);

    const pre65GraphData = this.formatPre65GraphData();

    if (!this.isPost65) {
      this.makeSeries(
        'RPP',
        'Pre65RPP',
        false,
        xAxis,
        yAxis,
        'Pre 65',
        pre65GraphData
      );
      if (this.retirementEvents.SPPFlag || this.retirementEvents.EPSFlag) {
        this.makeSeries(
          'SPP (SPS or ESPS)',
          'Pre65SERP',
          true,
          xAxis,
          yAxis,
          '',
          pre65GraphData
        );
      }
      this.makeSeries(
        'Bridge To 65',
        'Pre65Bridge',
        true,
        xAxis,
        yAxis,
        '',
        pre65GraphData
      );
    }

    this.makeSeries('RPP', 'Post65RPP', false, xAxis, yAxis, 'Post 65');
    if (this.retirementEvents.SPPFlag || this.retirementEvents.EPSFlag) {
      this.makeSeries('SPP (SPS or ESPS)', 'Post65SERP', true, xAxis, yAxis);
    }

    this.makeSeries('Bridge To 65', 'Post65Bridge', true, xAxis, yAxis);

    if (this.hasSpouse()) {
      this.makeSeries(
        'RPP',
        'Post65Surivor',
        false,
        xAxis,
        yAxis,
        'Survivor Post 65'
      );
      this.makeSeries(
        'SPP (SPS or ESPS)',
        'Post65SurivorSERP',
        true,
        xAxis,
        yAxis
      );
    }

    this.chart.appear(1000, 100);

    // Create a loading indicator
    this.indicator = this.root.container.children.push(
      am5.Container.new(this.root, {
        width: am5.p100,
        height: am5.p100,
        layer: 1000,
        background: am5.Rectangle.new(this.root, {
          fill: am5.color(0xffffff),
          fillOpacity: 0.7,
        }),
      })
    );

    this.indicator.children.push(
      am5.Label.new(this.root, {
        text: 'Calculating...',
        fontSize: 25,
        x: am5.p50,
        y: am5.p50,
        centerX: am5.p50,
        centerY: am5.p50,
      })
    );

    var hourglass = this.indicator.children.push(
      am5.Graphics.new(this.root, {
        width: 32,
        height: 32,
        x: am5.p50,
        y: am5.p50,
        centerX: am5.p50,
        centerY: am5.p50,
        dy: -45,
        draw: function (display) {
          display.drawEllipse(0, 0, 50, 50);
        },
      })
    );

    this.hourglassanimation = hourglass.animate({
      key: 'rotation',
      to: 180,
      loops: Infinity,
      duration: 2000,
      easing: am5.ease.inOut(am5.ease.cubic),
    });

    if (this.indicator) {
      this.toggleIndicator(false);
    }
  }

  isAgeGreaterThan65(dob: string, futureDate: string) {
    const birthDate = new Date(dob);
    const targetDate = new Date(futureDate);

    const sixtyFifthBirthday = new Date(birthDate);
    sixtyFifthBirthday.setFullYear(birthDate.getFullYear() + 65);
    return sixtyFifthBirthday <= targetDate;
  }

  formatPre65GraphData() {
    return this.graphData.filter((graph) => {
      return !this.isAgeGreaterThan65(
        this.currentUser.DOB.toString(),
        graph.year
      );
    });
  }

  toggleIndicator(display: boolean) {
    if (display) {
      this.hourglassanimation.play();
      this.indicator.show();
    } else {
      this.hourglassanimation.pause();
      this.indicator.hide();
    }
  }

  checkPre65Total(data: any): boolean {
    if (data.RetirementBenefits.Pre65Total === 0) {
      return false;
    }
    return true;
  }

  highlighColumn(ev: any) {
    this.selectedOptionalBenefits = this.retirementEvents.RetirementEvents.find(
      (element: any) => {
        if (
          this.formatDate(element.RetirementDate) ==
          ev.target.dataItem.dataContext.year
        ) {
          return element.RetirementBenefits;
        }
      }
    );

    if (this.checkPre65Total(this.selectedOptionalBenefits)) {
      if (!this.optionalBenefitsColumns.includes('Pre65Total')) {
        this.optionalBenefitsColumns.splice(1, 0, 'Pre65Total');
      }
    } else {
      if (this.optionalBenefitsColumns.includes('Pre65Total')) {
        const index = this.optionalBenefitsColumns.indexOf('Pre65Total');
        this.optionalBenefitsColumns.splice(index, 1);
      }
    }

    this.chart.series.each(function (series) {
      if (series instanceof am5xy.ColumnSeries) {
        series.columns.each(function (column: any) {
          if (
            column.dataItem.dataContext.year ==
            ev.target.dataItem.dataContext.year
          ) {
            column.states.apply('active');
          } else {
            column.states.apply('inactive');
          }
        });
      }
    });
  }

  makeSeries(
    name: string,
    fieldName: string,
    stacked: boolean,
    xAxis: any,
    yAxis: any,
    bulletText: string = '',
    data: any[] | null = null
  ) {
    var series = this.chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        stacked: stacked,
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: fieldName,
        categoryXField: 'year',
        maskBullets: false,
        interactive: true,
      })
    );

    series.columns.template.setAll({
      tooltipText: '${valueY}',
      tooltipY: am5.percent(10),
      fill: am5.color(this.getColor(fieldName)),
      strokeOpacity: 0,
    });

    series.columns.template.events.on('click', (event) => {
      this.highlighColumn(event);
    });

    if (fieldName == 'Pre65Bridge') {
      series.bullets.push(() => {
        return am5.Bullet.new(this.root, {
          locationX: 0.5,
          locationY: 1,
          sprite: am5.Label.new(this.root, {
            paddingTop: -10,
            text: '${Pre65Total}',
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            textAlign: 'center',
            populateText: true,
          }),
        });
      });
    }

    if (fieldName == 'Post65Bridge') {
      series.bullets.push(() => {
        return am5.Bullet.new(this.root, {
          locationX: 0.5,
          locationY: 1,
          sprite: am5.Label.new(this.root, {
            paddingTop: -10,
            text: '${Post65Total}',
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            textAlign: 'center',
            populateText: true,
          }),
        });
      });
    }

    if (fieldName == 'Post65SurivorSERP') {
      series.bullets.push(() => {
        return am5.Bullet.new(this.root, {
          locationX: 0.5,
          locationY: 1,
          sprite: am5.Label.new(this.root, {
            paddingTop: -10,
            text: '${SurvivorPost65Total}',
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            textAlign: 'center',
            populateText: true,
          }),
        });
      });
    }

    if (bulletText.length > 0) {
      series.bullets.push(() => {
        return am5.Bullet.new(this.root, {
          locationY: 0,
          sprite: am5.Label.new(this.root, {
            text: bulletText,
            fill: am5.color(0x000000),
            populateText: true,
            breakWords: false,
            rotation: 0,
            paddingLeft: -30,
            height: 20,
            width: 40,
            fontSize: 13,
            textAlign: 'center',
            oversizedBehavior: 'wrap',
          }),
        });
      });
    }

    series.columns.template.states.create('active', {
      fillOpacity: 1,
      stroke: am5.color('#FFFFFF'),
      strokeWidth: 0,
    });

    series.columns.template.states.create('inactive', {
      fillOpacity: 0.3,
      stroke: am5.color('#FFFFFF'),
      strokeWidth: 0,
    });

    if (data) {
      series.data.setAll(data);
    } else {
      series.data.setAll(this.graphData);
    }
  }

  getColor(fieldName: string) {
    if (fieldName.includes('Bridge')) {
      return '#BED73D';
    } else if (fieldName.includes('SERP')) {
      return '#455660';
    } else if (fieldName.includes('RPP')) {
      return '#017DC3';
    } else if (fieldName.toLocaleLowerCase().includes('post65surivor')) {
      return '#017DC3';
    } else {
      return '#000000';
    }
  }

  openRppAndSPSDialog(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Average Pensionable Earnings (RPP)',
        dialogContent: AveragePensionableEarningsSpsInfoComponent,
      },
    });
  }

  departureDateDialog(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Departure Date',
        dialogContent: DepartureDateComponent,
      },
    });
  }

  openRppDialog(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Average Pensionable Earnings (RPP)',
        dialogContent: AveragePensionableEarningsInfoComponent,
      },
    });
  }

  openEspsDialog(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Average Pensionable Earnings (ESPS)',
        dialogContent: AveragePensionableEarningsEspsInfoComponent,
      },
    });
  }

  openYearlySalaryIncrease(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Yearly Salary Increase',
        dialogContent: YearlySalaryInfoComponent,
      },
    });
  }

  futurePercentageofWorkDialog(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Future Percentage of Work',
        dialogContent: FuturePercentageInfoComponent,
      },
    });
  }

  effectiveDateDialog(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Effective Date',
        dialogContent: EffectiveDateInfoComponent,
      },
    });
  }

  openPensionPaymentDialog(): void {
    if (!this.hasSpouse()) {
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: {
          title: 'Form of Pension Payment',
          dialogContent: FormOfPensionPaymentInfoComponent,
        },
      });
    } else {
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: {
          title: 'Form of Pension Payment',
          dialogContent: FormOfPensionPaymentMarriedInfoComponent,
        },
      });
    }
  }

  openRetirementDateDialog(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Add Retirement Date',
        dialogContent: AddRetirementDateInfoComponent,
      },
    });
  }

  openComparisonRetirementTypes(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Comparison of Retirement Types',
        dialogContent: ComparisonRetirementTypesComponent,
      },
    });
  }

  openComparisonRetirementTypesEsps(): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'Comparison of Retirement Types',
        dialogContent: ComparisonRetirementTypesEspsComponent,
      },
    });
  }

  print() {
    this.inPrint = true;
    const chartDiv = document.getElementById('chartdiv');
    if (chartDiv) {
      chartDiv.style.width = '65%';
      chartDiv.style.marginLeft = '-100px';
      setTimeout(() => {
        window.print();
        chartDiv.style.width = '100%';
        chartDiv.style.marginLeft = '0';
        this.inPrint = false;
      }, 500);
    } else {
      setTimeout(() => {
        window.print();
        this.inPrint = false;
      }, 500);
    }
  }
}
